import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  CalendarDays,
  Boxes,
  Users,
  LogOut,
  Menu,
  X,
  ClipboardList,
  Truck,
  Receipt,
  CalendarOff,
  Search,
  AlertTriangle,
  Settings as SettingsIcon,
  Inbox,
  UserCircle,
  FileBadge,
  GraduationCap,
  FolderOpen,
  Wallet,
  LifeBuoy,
  FileText,
  Calculator,
} from "lucide-react";
import { BRAND_LOGO_URL } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import NotificationButton from "@/components/app/NotificationButton";
import PushPromptBanner from "@/components/app/PushPromptBanner";
import LanguageSwitcher from "@/components/app/LanguageSwitcher";

const adminNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link" },
  { to: "/lavori", label: "Lavori", icon: Boxes, testid: "nav-jobs-link" },
  { to: "/preventivi", label: "Preventivi", icon: FileText, testid: "nav-quotes-link" },
  { to: "/listino", label: "Listino Costi", icon: Calculator, testid: "nav-price-list-link" },
  { to: "/calendario", label: "Calendario", icon: CalendarDays, testid: "nav-calendar-link" },
  { to: "/dipendenti", label: "Dipendenti", icon: Users, testid: "nav-employees-link" },
  { to: "/mezzi", label: "Mezzi", icon: Truck, testid: "nav-vehicles-link" },
  { to: "/storico-mezzi", label: "Storico Mezzi", icon: Search, testid: "nav-vehicle-history-link" },
  { to: "/anomalie", label: "Anomalie Mezzi", icon: AlertTriangle, testid: "nav-anomalies-link" },
  { to: "/presenze", label: "Presenze", icon: ClipboardList, testid: "nav-timesheet-link" },
  { to: "/nota-spese", label: "Nota Spese", icon: Receipt, testid: "nav-expenses-link" },
  { to: "/ferie", label: "Ferie & Riposo", icon: CalendarOff, testid: "nav-leaves-link" },
  { to: "/richieste", label: "Richieste", icon: Inbox, testid: "nav-requests-link" },
  { to: "/documenti", label: "Documenti", icon: FileBadge, testid: "nav-documents-link" },
  { to: "/formazione", label: "Formazione", icon: GraduationCap, testid: "nav-training-link" },
  { to: "/archivio", label: "Archivio", icon: FolderOpen, testid: "nav-archive-link" },
  { to: "/paghe", label: "Paghe", icon: Wallet, testid: "nav-payroll-link" },
  { to: "/assistenza", label: "Assistenza", icon: LifeBuoy, testid: "nav-support-link" },
  { to: "/impostazioni", label: "Impostazioni", icon: SettingsIcon, testid: "nav-settings-link" },
];
const employeeNav = [
  { to: "/area-personale", label: "Area Personale", icon: UserCircle, testid: "nav-personal-area-link" },
  { to: "/formazione", label: "Formazione", icon: GraduationCap, testid: "nav-training-link" },
  { to: "/archivio", label: "Archivio", icon: FolderOpen, testid: "nav-archive-link" },
  { to: "/miei-lavori", label: "I Miei Lavori", icon: ClipboardList, testid: "nav-my-jobs-link" },
  { to: "/presenze", label: "Le Mie Ore", icon: CalendarDays, testid: "nav-timesheet-link" },
  { to: "/nota-spese", label: "Nota Spese", icon: Receipt, testid: "nav-expenses-link" },
  { to: "/ferie", label: "Ferie & Riposo", icon: CalendarOff, testid: "nav-leaves-link" },
  { to: "/assistenza", label: "Assistenza", icon: LifeBuoy, testid: "nav-support-link" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = user?.role === "admin"
    ? adminNav.filter((i) => !(user.office && ["/paghe", "/impostazioni"].includes(i.to)))
    : employeeNav;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex w-72 flex-col bg-slate-900 text-slate-100 border-r border-slate-800">
        <div className="px-6 py-6 border-b border-slate-800 bg-white">
          <div className="flex items-center justify-center">
            <img src={BRAND_LOGO_URL} alt="In & Out Traslochi" className="h-14 w-auto object-contain" />
          </div>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-150 ${
                  isActive
                    ? "bg-sky-600 text-white font-semibold shadow-lg shadow-sky-600/30"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/70"
                }`
              }
            >
              <it.icon className="w-4.5 h-4.5" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="px-3 py-3 rounded-xl bg-slate-800/50 mb-3">
            <div className="text-xs text-slate-400">Collegato come</div>
            <div className="text-sm font-semibold text-white truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-[11px] text-sky-400 uppercase tracking-wide font-semibold mt-0.5">{user?.role === "admin" ? "Amministratore" : "Dipendente"}</div>
          </div>
          <div className="mb-3">
            <NotificationButton />
          </div>
          <div className="mb-3">
            <LanguageSwitcher dark />
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            data-testid="logout-button"
            className="w-full border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
        </div>
      </aside>

      {/* Mobile top bar — abbassata leggermente sotto il notch */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white text-slate-900 border-b border-slate-200 shadow-sm" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
        <div className="h-20 pt-3 px-4 flex items-center justify-between">
          <img src={BRAND_LOGO_URL} alt="In & Out Traslochi" className="h-12 w-auto object-contain" />
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="p-3 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl"
            aria-label="Menu"
          >
            {open ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
          </button>
        </div>
        {open && (
          <div className="px-3 pb-6 space-y-1.5 bg-white border-t border-slate-100 max-h-[calc(100vh-5.75rem)] overflow-y-auto">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `flex items-center gap-4 px-5 py-4 rounded-2xl text-base font-semibold ${
                    isActive ? "bg-sky-600 text-white shadow-md shadow-sky-600/30" : "text-slate-800 hover:bg-slate-100"
                  }`
                }
              >
                <it.icon className="w-6 h-6" />
                {it.label}
              </NavLink>
            ))}
            <div className="pt-3">
              <NotificationButton />
            </div>
            <div className="pt-3">
              <LanguageSwitcher testid="language-switcher-mobile" />
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full mt-3 h-12 text-base"
              data-testid="logout-button-mobile"
            >
              <LogOut className="w-5 h-5 mr-2" /> Esci
            </Button>
          </div>
        )}
      </div>

      <main className="flex-1 lg:ml-0 pt-24 lg:pt-0 min-w-0 pb-safe">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-10">
          <PushPromptBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
